import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Order } from '@/types/index';

// Track seen item IDs per session in localStorage
function getSeenItemIdsAll(): Record<string, string[]> {
    try {
        return JSON.parse(localStorage.getItem('seen_items_all_sessions') || '{}');
    } catch {
        return {};
    }
}

function setSeenItemIdsAll(obj: Record<string, string[]>) {
    localStorage.setItem('seen_items_all_sessions', JSON.stringify(obj));
}

export const useItemAcknowledgment = (orders: Order[], refetch: () => Promise<void>) => {
    const { toast } = useToast();
    const [seenItemIdsAll, setSeenItemIdsAllState] = useState<Record<string, string[]>>(getSeenItemIdsAll());

    // Sync with localStorage on mount
    useEffect(() => {
        setSeenItemIdsAllState(getSeenItemIdsAll());
    }, []);

    const handleMarkItemSeen = useCallback(async (sessionId: string, itemId: string) => {
        // Optimistic update
        const currentSeen = seenItemIdsAll[sessionId] || [];
        if (currentSeen.includes(itemId)) return;

        const updatedSeen = [...currentSeen, itemId];
        const updated = { ...seenItemIdsAll, [sessionId]: updatedSeen };

        setSeenItemIdsAllState(updated);
        setSeenItemIdsAll(updated);

        try {
            // Update in Supabase
            const { error } = await supabase
                .from('order_items')
                .update({ is_acknowledged: true })
                .eq('id', itemId);

            if (error) throw error;

            // Refetch to get updated data
            await refetch();

            toast({
                title: "Success",
                description: "Item marked as seen",
            });
        } catch (error) {
            console.error('Error updating item acknowledgment:', error);
            toast({
                title: "Error",
                description: "Failed to mark item as seen",
                variant: "destructive"
            });
        }
    }, [seenItemIdsAll, refetch, toast]);

    // Check for new items (unseen and unacknowledged)
    const checkHasNewItems = useCallback((sessionId: string, sessionOrders: Order[]) => {
        const seenIds = seenItemIdsAll[sessionId] || [];
        return sessionOrders.some(order =>
            order.order_items.some(item =>
                !item.is_acknowledged && !seenIds.includes(item.id) && !item.is_removed
            )
        );
    }, [seenItemIdsAll]);

    // Auto-mark initial items as seen (optional logic from original code)
    useEffect(() => {
        const updates: Record<string, string[]> = {};
        let hasUpdates = false;

        orders.forEach(order => {
            const sessionId = order.table_session_id || order.id;
            // If this session has no seen items yet, mark all items from the first order as seen?
            // This logic was in the original code to prevent marking old items as new on first load.
            // For now, we'll skip this auto-marking to avoid complexity, or implement if needed.
        });
    }, [orders]);

    return {
        handleMarkItemSeen,
        checkHasNewItems
    };
};
